/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localize } from '../translations.js';
import { withEnterKey } from '../internal/with-enter-key.js';
import { SupportedLocale } from '../model.js';
import * as Assets from '../assets.js';
/**
 * WebComponent.
 */
let PwcAiChatDockedPanel = class PwcAiChatDockedPanel extends LitElement {
    constructor() {
        super(...arguments);
        this.locale = SupportedLocale.en_US;
        this.isMobile = false;
    }
    /**
     * Render the restore button.
     */
    _renderRestoreButton() {
        // on mobile, do not render the restore button.
        if (this.isMobile) {
            return html ``;
        }
        // return restore button.
        return html `<div class="header-button">
      ${Assets.iconMaximize()}
      <pwc-tooltip>${localize(this.locale, 'ai.chat.expand')}</pwc-tooltip>
    </div>`;
    }
    /**
     * Render the close button.
     */
    _renderCloseButton() {
        return html ` <div
      class="header-button"
      @click=${() => {
            this._handleCloseDockedPanel();
        }}
      @keypress=${(event) => {
            withEnterKey(event, () => this._handleCloseDockedPanel());
        }}
    >
      ${Assets.iconClose()}
      <pwc-tooltip>${localize(this.locale, 'ai.chat.close')}</pwc-tooltip>
    </div>`;
    }
    /**
     * @override
     */
    render() {
        // render.
        return html `
      <div class="panel">
        <div class="title">
          <div
            class="restore-area"
            @click=${() => {
            this._handleRestoreDockedPanel();
        }}
            @keypress=${(event) => {
            withEnterKey(event, () => this._handleRestoreDockedPanel());
        }}
          >
            ${Assets.iconCircleAi()}
            <div class="text">${localize(this.locale, 'ai.chat.pros-ai')}</div>
            <div class="spacer">&nbsp;</div>
            ${this._renderRestoreButton()}
          </div>
          ${this._renderCloseButton()}
        </div>
      </div>
    `;
    }
    /**
     * Handle close the docked panel action.
     */
    _handleCloseDockedPanel() {
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-docked-panel-close'));
    }
    /**
     * Handle restore the docked panel action.
     */
    _handleRestoreDockedPanel() {
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-docked-panel-restore'));
    }
};
// styles.
PwcAiChatDockedPanel.styles = css `
    :host {
      /* font size */
      --pwc-ai-chat-docked-panel-font-size: var(--pwc-font-size-large, 1.25rem);

      /* sizes */
      --pwc-ai-chat-docked-panel-weight: 47px;
      --pwc-ai-chat-docked-panel-button-size: 15px;
      --pwc-ai-chat-docked-panel-bottom: -1px;
      --pwc-ai-chat-docked-panel-right: 15px;

      /* border */
      --pwc-ai-chat-docked-panel-border-radius: 3px 3px 0px 0px;
      --pwc-ai-chat-docked-panel-box-shadow: 0px 1px 3px 1px rgba(0, 0, 0, 0.05);
      --pwc-ai-chat-docked-panel-border: 1px solid #e0e0e0;

      /* host css properties */
      position: fixed;
      display: flex;
      flex: none;
      width: var(--pwc-ai-chat-docked-panel-width);
      box-shadow: 0 0 16px hsl(240 3.8% 46.1% / 12%);
    }

    .panel {
      display: flex;
      flex: auto;
      flex-direction: column;
      border: var(--pwc-ai-chat-docked-panel-border);
      border-radius: var(--pwc-ai-chat-docked-panel-border-radius);
      box-shadow: var(--pwc-ai-chat-docked-panel-box-shadow);
      background-color: white;
    }

    .panel .title {
      height: var(--pwc-ai-chat-docked-panel-height);
      display: flex;
      align-items: center;
      flex-direction: row;
      padding: 10px 15px;
    }

    .panel .title svg.icon-circle-ai {
      flex: none;
      width: 22px;
      height: 22px;
      margin-right: 8px;
    }

    .panel .title .text {
      flex: none;
      background: linear-gradient(
        350deg,
        #26a9d3 7.62%,
        #0b3cea 25.63%,
        #3d17dc 41.64%,
        #611096 59.71%,
        #a82c35 77.77%,
        #ff7e3b 88.44%
      );
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-size: var(--pwc-ai-chat-docked-panel-font-size);
      font-weight: 600;
      white-space: nowrap;
      padding-top: 1px;
    }

    .panel .title .spacer {
      flex: auto;
      width: 100%;
      height: 1rem;
    }

    .panel .title .header-button {
      display: flex;
      flex: auto;
    }

    .panel .title .header-button svg {
      flex: none;
      width: var(--pwc-ai-chat-docked-panel-button-size);
      height: var(--pwc-ai-chat-docked-panel-button-size);
      margin: 0px 6px 0px 6px;
      color: #777777;
      cursor: pointer;
    }

    .panel .title .header-button svg:hover {
      color: #000000;
    }

    .panel .restore-area {
      display: flex;
      cursor: pointer;
      flex-direction: row;
      align-items: center;
    }
  `;
__decorate([
    property({ attribute: true, type: String })
], PwcAiChatDockedPanel.prototype, "locale", void 0);
__decorate([
    property({ attribute: 'is-mobile', type: Boolean })
], PwcAiChatDockedPanel.prototype, "isMobile", void 0);
PwcAiChatDockedPanel = __decorate([
    customElement('pwc-ai-chat-docked-panel')
], PwcAiChatDockedPanel);
export { PwcAiChatDockedPanel };
//# sourceMappingURL=pwc-ai-chat-docked-panel.js.map